import React, { useState } from 'react';
import { Table, Button, Badge, Dropdown, SplitButton, Modal, Form, Spinner, Card } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { Candidate, Stage } from '../../types/types';
import moment from 'moment';
import SendEmailModal from '../admin/SendEmailModal';
import { getNextStage } from '../../utils/stageUtils';

interface CandidateListProps {
    candidates: Candidate[];
    // All stages, including inactive, so a candidate in an inactive stage can still be moved on
    stages?: Stage[];
    onStageChange: (candidateId: string, newStageId: string, notes?: string) => Promise<void>;
}

const CandidateList: React.FC<CandidateListProps> = ({
    candidates,
    stages = [],
    onStageChange
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [showStageModal, setShowStageModal] = useState<boolean>(false);
    const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
    const [targetStageId, setTargetStageId] = useState<string>('');
    const [stageChangeNotes, setStageChangeNotes] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
    const [emailCandidate, setEmailCandidate] = useState<Candidate | null>(null);

    const handleViewCandidate = (candidateId: string) => {
        navigate(`/candidates/${candidateId}`, { state: { from: location.pathname } });
    };

    const openStageChangeModal = (candidate: Candidate, stageId: string) => {
        setSelectedCandidate(candidate);
        setTargetStageId(stageId);
        setStageChangeNotes('');
        setShowStageModal(true);
    };

    const handleStageChange = async () => {
        if (!selectedCandidate || !targetStageId) return;

        setIsSubmitting(true);

        try {
            await onStageChange(selectedCandidate.id, targetStageId, stageChangeNotes);
            setShowStageModal(false);
        } catch (error) {
            console.error('Error changing stage:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStageBadgeVariant = (daysInStage: number): string => {
        if (daysInStage > 10) return 'danger';
        if (daysInStage > 5) return 'warning';
        return 'success';
    };

    // Stages that can be moved to from any stage (e.g. Rejected), excluding the current and next stage
    const getOtherStages = (candidate: Candidate, nextStage?: Stage): Stage[] =>
        stages.filter(stage =>
            stage.isActive &&
            stage.canMoveToFromAnyStage === true &&
            stage.id !== candidate.stageId &&
            stage.id !== nextStage?.id
        );

    const getAvailableStages = (candidate: Candidate): Stage[] => {
        const nextStage = getNextStage(stages, candidate.stageId);
        return [...(nextStage ? [nextStage] : []), ...getOtherStages(candidate, nextStage)];
    };

    const renderStageButton = (candidate: Candidate) => {
        const nextStage = getNextStage(stages, candidate.stageId);
        const otherStages = getOtherStages(candidate, nextStage);

        if (!nextStage && otherStages.length === 0) {
            return (
                <Button variant="success" size="sm" disabled title="No further stages">
                    <i className="bi bi-arrow-right me-1"></i>
                    Stage
                </Button>
            );
        }

        const label = (
            <>
                <i className="bi bi-arrow-right me-1"></i>
                {nextStage ? nextStage.name : 'Stage'}
            </>
        );
        const otherItems = otherStages.map(stage => (
            <Dropdown.Item key={stage.id} onClick={() => openStageChangeModal(candidate, stage.id)}>
                {stage.name}
            </Dropdown.Item>
        ));

        if (!nextStage) {
            return (
                <Dropdown>
                    <Dropdown.Toggle variant="success" size="sm" id={`stage-${candidate.id}`} title="Change stage">
                        {label}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>{otherItems}</Dropdown.Menu>
                </Dropdown>
            );
        }

        if (otherStages.length === 0) {
            return (
                <Button
                    variant="success"
                    size="sm"
                    onClick={() => openStageChangeModal(candidate, nextStage.id)}
                    title={`Move to ${nextStage.name}`}
                >
                    {label}
                </Button>
            );
        }

        return (
            <SplitButton
                variant="success"
                size="sm"
                id={`stage-${candidate.id}`}
                title={label}
                onClick={() => openStageChangeModal(candidate, nextStage.id)}
                align="end"
            >
                {otherItems}
            </SplitButton>
        );
    };


    const openEmailModal = (candidate: Candidate) => {
        setEmailCandidate(candidate);
        setShowEmailModal(true);
    };

    const handleEmailSent = () => {
        setShowEmailModal(false);
        setEmailCandidate(null);
        //TODO - toast
    };

    return (
        <div className="d-flex flex-column" style={{ minHeight: 'calc(50vh)' }}>
            <Card className="flex-grow-1 mb-0 border-0 shadow-sm">
                <Card.Body className="p-0">
                    <div className="table-responsive" style={{ height: 'calc(50vh)' }}>
                        <Table hover className="align-middle mb-0">
                            <thead className="table-light sticky-top">
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Job</th>
                                    <th>Days in Stage</th>
                                    <th className="text-center">Interview</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {candidates.map(candidate => (
                                    <tr key={candidate.id}>
                                        <td>
                                            <div className="fw-bold">{candidate.lastName}, {candidate.firstName}</div>
                                            <div className="small text-muted">{candidate.currentStage}</div>
                                        </td>
                                        <td>
                                            <a href={`mailto:${candidate.email}`}>{candidate.email}</a>
                                        </td>
                                        <td>
                                            {candidate.phone ? (
                                                <a href={`tel:${candidate.phone}`}>{candidate.phone}</a>
                                            ) : (
                                                <span className="text-muted">No phone</span>
                                            )}
                                        </td>
                                        <td>
                                            {candidate.jobTitle || <span className="text-muted">No job</span>}
                                        </td>
                                        <td>
                                            <Badge
                                                bg={getStageBadgeVariant(candidate.daysInCurrentStage)}
                                                className="px-2 py-1"
                                            >
                                                {candidate.daysInCurrentStage} days
                                            </Badge>
                                        </td>
                                        <td className="text-center">
                                            {candidate.interviewDate ? (
                                                <div>
                                                    <Badge bg="primary" className="px-2 py-1">
                                                        {moment(candidate.interviewDate).format('DD MMM YYYY')}
                                                    </Badge>
                                                    {moment().isSame(moment(candidate.interviewDate), 'day') && (
                                                        <Badge bg="warning" text="dark" className="ms-1">Today</Badge>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-muted">No interview</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="d-flex justify-content-end">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-1"
                                                    onClick={() => handleViewCandidate(candidate.id)}
                                                    title="View candidate details"
                                                >
                                                    <i className="bi bi-eye"></i>
                                                </Button>

                                                <Button
                                                    variant="outline-info"
                                                    size="sm"
                                                    className="me-1"
                                                    onClick={() => openEmailModal(candidate)}
                                                    title="Send email to candidate"
                                                >
                                                    <i className="bi bi-envelope"></i>
                                                </Button>

                                                {renderStageButton(candidate)}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {candidates.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="text-center py-5">
                                            <div className="text-muted">
                                                <i className="bi bi-inbox fs-2 d-block mb-2"></i>
                                                No candidates found
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            {/* Stage Change Modal */}
            <Modal show={showStageModal} onHide={() => setShowStageModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="bi bi-arrow-right me-2"></i>
                        Change Candidate Stage
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCandidate && (
                        <>
                            <p>
                                Change stage for <strong>{selectedCandidate.firstName} {selectedCandidate.lastName}</strong>
                            </p>

                            <Form.Group className="mb-3">
                                <Form.Label>Current Stage</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={selectedCandidate.currentStage}
                                    disabled
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>New Stage</Form.Label>
                                <Form.Select
                                    value={targetStageId}
                                    onChange={(e) => setTargetStageId(e.target.value)}
                                    required
                                >
                                    <option value="">Select a stage</option>
                                    {getAvailableStages(selectedCandidate).map(stage => (
                                        <option
                                            key={stage.id}
                                            value={stage.id}
                                        >
                                            {stage.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Notes</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Add notes about this stage change (optional)"
                                    value={stageChangeNotes}
                                    onChange={(e) => setStageChangeNotes(e.target.value)}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowStageModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleStageChange}
                        disabled={!targetStageId || isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-check-circle me-2"></i>
                                Save Changes
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Email Modal */}
            {emailCandidate && (
                <SendEmailModal
                    show={showEmailModal}
                    onHide={() => setShowEmailModal(false)}
                    candidateId={emailCandidate.id}
                    candidateName={`${emailCandidate.firstName} ${emailCandidate.lastName}`}
                    candidateEmail={emailCandidate.email}
                    stageId={emailCandidate.stageId}
                    onEmailSent={handleEmailSent}
                />
            )}
        </div>
    );
};

export default CandidateList;